// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import { Box, Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

/**
 * Petits composants partagés par la liste des serveurs, la ligne d'un serveur
 * et le détail d'un serveur d'établissement.
 */

export function StepIcon({ status }) {
  if (status === 'ok') return <CheckCircleIcon fontSize="small" color="success" />;
  if (status === 'error') return <ErrorIcon fontSize="small" color="error" />;
  // Pas `color="disabled"` : MUI y pose `action.disabled` (35 % d'opacité),
  // invisible sur le panneau creusé du détail. Le gris-bleu plein de la charte
  // reste secondaire à côté du vert et du rouge, mais il se voit.
  return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
}

export function SecretField({ label, value, onCopy }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
          {value || '—'}
        </Typography>
        {value && (
          <Tooltip title="Copier">
            <IconButton size="small" onClick={() => onCopy(value)}>
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Grid>
  );
}

// Canal de mise à jour d'un serveur d'établissement : `GET /api/kits` rend
// `mise_a_jour`, tiré du maj.json que le serveur envoie avec sa santé. Le
// libellé d'un échec vient du backend (detector_health.ETATS_ECHEC_MISE_A_JOUR,
// codes 11 à 14 d'update.py) ; ici, seulement la couleur, pour qu'il n'y ait
// qu'une traduction des codes.
const UPDATE_FAILURE_COLOR = { 11: 'warning', 12: 'warning', 13: 'error', 14: 'error' };

export function UpdateFailure({ echec: failure }) {
  if (!failure) return null;
  return (
    <Tooltip title={[failure.version && `version ${failure.version}`, failure.motif].filter(Boolean).join(' : ')}>
      <Chip size="small" color={UPDATE_FAILURE_COLOR[failure.code] || 'error'} label={failure.libelle} />
    </Tooltip>
  );
}
